import { useQuery } from '@apollo/client/react';
import { Trans, useTranslation } from 'next-i18next/pages';
import React from 'react';

import { JobBoardFiltersInput } from '@pflegia/data-access';
import { ReactComponent as BarChartIcon } from '@pflegia/ui/icons/bar-chart.svg';
import { getCityFallback } from '@pflegia/util/functions/job-position/helpers';

import { GET_JOB_POSITIONS_SALARY_ESTIMATIONS } from '@shared/graphql/operations/job-positions-salary-estimations';
import { GET_LLP_MARKET_STATS } from '@shared/graphql/operations/llp-market-stats';

type MarketStatsGeoTextProps = Partial<Pick<JobBoardFiltersInput, 'jobTitleIds' | 'locations'>> & {
  jobTitle?: string;
  totalJobs?: number;
};

const de = (value: number) => value.toLocaleString('de-DE');

export const MarketStatsGeoText: React.FC<MarketStatsGeoTextProps> = ({ jobTitleIds, locations, jobTitle, totalJobs }) => {
  const { t } = useTranslation('jobListings');
  const skip = jobTitleIds?.length !== 1 || locations?.length !== 1 || !locations[0]?.city;
  const city = getCityFallback(locations?.[0]);

  const { data: statsData } = useQuery(GET_LLP_MARKET_STATS, { skip, variables: { filters: { jobTitleIds, locations } } });
  const { data: salaryData } = useQuery(GET_JOB_POSITIONS_SALARY_ESTIMATIONS, { skip, variables: { filters: { jobTitleIds, locations } } });

  const stats = statsData?.data;
  const salary = salaryData?.data;
  if (skip || !stats || totalJobs === undefined) return null;

  const values = {
    count: de(totalJobs),
    jobTitle,
    city,
    employers: de(stats.uniqueEmployers),
    avgSalary: salary?.averageSalary ? de(salary.averageSalary) : '',
    salaryMin: salary?.averageMinSalary ? de(salary.averageMinSalary) : '',
    salaryMax: salary?.averageMaxSalary ? de(salary.averageMaxSalary) : '',
  };

  return (
    <section className='my-4'>
      <div className='bg-primary/5 flex gap-4 rounded-3xl p-6'>
        <span className='bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-full'>
          <BarChartIcon className='h-6 w-6' aria-hidden />
        </span>
        <div>
          <h3 className='font-semibold'>{t('marketStats.geoHeading', { jobTitle, city })}</h3>
          <p className='text-text-secondary mt-2 text-sm md:text-[15px]'>
            <Trans i18nKey='marketStats.geo' t={t} values={values} components={{ b: <span className='text-primary font-semibold' /> }} />
            {Boolean(salary?.averageSalary) && (
              <Trans
                i18nKey='marketStats.geoSalary'
                t={t}
                values={values}
                components={{ b: <span className='text-primary font-semibold' /> }}
              />
            )}
          </p>
        </div>
      </div>
    </section>
  );
};
